const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface FetchOptions {
    method?: string;
    body?: any;
    params?: Record<string, any>;
}

export function parsePaginatedResponse<T = any>(data: any): { items: T[]; total: number } {
    if (Array.isArray(data) && data.length === 2 && typeof data[1] === 'number') {
        return { items: data[0] as T[], total: data[1] };
    }
    if (data?.data && Array.isArray(data.data)) {
        return { items: data.data as T[], total: data.total || data.data.length };
    }
    if (Array.isArray(data)) {
        return { items: data as T[], total: data.length };
    }
    return { items: [], total: 0 };
}

class ApiClient {
    private getToken(): string | null {
        return localStorage.getItem('auth_token');
    }

    private getHeaders(extraHeaders: Record<string, string> = {}): Record<string, string> {
        const token = this.getToken();
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            ...extraHeaders,
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        const impersonated = localStorage.getItem('impersonated_tenant_id');
        if (impersonated) {
            headers['X-Tenant-ID'] = impersonated;
        }
        return headers;
    }

    private buildUrl(path: string, params?: Record<string, any>): string {
        const url = new URL(`${API_BASE}${path}`, window.location.origin);
        if (params) {
            Object.entries(params).forEach(([key, value]) => {
                if (value !== undefined && value !== null) {
                    url.searchParams.append(key, String(value));
                }
            });
        }
        return url.toString();
    }

    private async request<T>(path: string, options?: FetchOptions): Promise<T> {
        const url = this.buildUrl(path, options?.params);
        const response = await fetch(url, {
            method: options?.method || 'GET',
            headers: this.getHeaders(options?.body?._headers),
            body: options?.body ? JSON.stringify(options.body) : undefined,
        });
        if (!response.ok) {
            const error = await response.json().catch(() => ({ message: response.statusText }));
            const requestError = new Error(error.message || response.statusText) as Error & { status?: number };
            requestError.status = response.status;
            throw requestError;
        }
        return response.json();
    }

    // ─── AUTH ─────────────────────────────────────────────────────────────────
    login(email: string, password: string) {
        return this.request<any>('/auth/login', { method: 'POST', body: { email, password } });
    }

    register(data: any) {
        return this.request<any>('/auth/register', { method: 'POST', body: data });
    }

    logout() {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
    }

    // ─── CAMPAIGNS ────────────────────────────────────────────────────────────
    getCampaigns(tenantId: string, page = 1, limit = 20, status?: string) {
        return this.request<any>('/admin/campaigns', { params: { tenantId, page, limit, status } });
    }

    getCampaign(id: string) {
        return this.request<any>(`/admin/campaigns/${id}`);
    }

    createCampaign(data: any) {
        return this.request<any>('/admin/campaigns', { method: 'POST', body: data });
    }

    updateCampaign(id: string, data: any) {
        return this.request<any>(`/admin/campaigns/${id}`, { method: 'PATCH', body: data });
    }

    updateCampaignStatus(id: string, status: string) {
        return this.request<any>(`/admin/campaigns/${id}/status`, { method: 'PATCH', body: { status } });
    }

    deleteCampaign(id: string) {
        return this.request<any>(`/admin/campaigns/${id}`, { method: 'DELETE' });
    }

    upgradeCampaign(id: string, planCode: string) {
        return this.request<any>(`/admin/campaigns/${id}/upgrade`, { method: 'POST', body: { plan_code: planCode } });
    }

    extendCampaign(id: string, days: number) {
        return this.request<any>(`/admin/campaigns/${id}/extend`, { method: 'POST', body: { days } });
    }

    getCampaignMetrics(id: string, tenantId: string, startDate?: string, endDate?: string) {
        return this.request<any>(`/admin/campaigns/${id}/metrics`, { params: { tenantId, startDate, endDate } });
    }

    // ─── GAME TYPES ───────────────────────────────────────────────────────────
    getGameTypes() {
        return this.request<any>('/admin/game-types');
    }

    // ─── GAME TEMPLATES ───────────────────────────────────────────────────────
    getGameTemplates(page = 1, limit = 20) {
        return this.request<any>('/admin/game-templates', { params: { page, limit } });
    }

    getGameTemplate(id: string) {
        return this.request<any>(`/admin/game-templates/${id}`);
    }

    createGameTemplate(data: any) {
        return this.request<any>('/admin/game-templates', { method: 'POST', body: data });
    }

    updateGameTemplate(id: string, data: any) {
        return this.request<any>(`/admin/game-templates/${id}`, { method: 'PATCH', body: data });
    }

    deleteGameTemplate(id: string) {
        return this.request<any>(`/admin/game-templates/${id}`, { method: 'DELETE' });
    }

    uploadGameTemplateZip(file: File) {
        const token = this.getToken();
        const url = this.buildUrl('/admin/game-templates/upload');
        const form = new FormData();
        form.append('file', file);
        return fetch(url, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        }).then(async res => {
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: res.statusText }));
                throw new Error(err.message || 'Upload failed');
            }
            return res.json();
        });
    }

    uploadGameAsset(file: File, type: 'logo' | 'background' | 'background_mobile' | 'background_desktop' | 'card_back') {
        const token = this.getToken();
        const url = this.buildUrl('/admin/upload/game-asset');
        const form = new FormData();
        form.append('image', file);
        form.append('type', type);
        return fetch(url, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            body: form,
        }).then(async res => {
            if (!res.ok) {
                const err = await res.json().catch(() => ({ message: res.statusText }));
                throw new Error(err.error?.message || err.message || 'Upload thất bại');
            }
            return res.json();
        });
    }

    getGameTemplatePreviewUrl(id: string): string {
        return `${API_BASE}/admin/game-templates/${id}/preview`;
    }

    // ─── GAME INSTANCES ───────────────────────────────────────────────────────
    getGameInstances(page = 1, limit = 20, status?: string, campaignId?: string) {
        return this.request<any>('/admin/game-instances', { params: { page, limit, status, campaignId } });
    }

    getGameInstance(id: string) {
        return this.request<any>(`/admin/game-instances/${id}`);
    }

    getGameInstancesByCampaign(campaignId: string) {
        return this.request<any>(`/admin/game-instances/campaign/${campaignId}`);
    }

    createGameInstance(data: any) {
        return this.request<any>('/admin/game-instances', { method: 'POST', body: data });
    }

    updateGameInstance(id: string, data: any) {
        return this.request<any>(`/admin/game-instances/${id}`, { method: 'PATCH', body: data });
    }

    deleteGameInstance(id: string) {
        return this.request<any>(`/admin/game-instances/${id}`, { method: 'DELETE' });
    }

    updateGameInstanceStatus(id: string, status: string) {
        return this.request<any>(`/admin/game-instances/${id}/status`, { method: 'PATCH', body: { status } });
    }

    /** Tạo game instance từ template — gọi đúng POST /admin/game-instances */
    createGameInstanceFromTemplate(templateId: string, campaignId: string, data: any) {
        return this.request<any>('/admin/game-instances', {
            method: 'POST',
            body: {
                game_template_id: templateId,
                campaign_id: campaignId,
                ...data,
            },
        });
    }

    // ─── GAME UPLOAD (ZIP) ────────────────────────────────────────────────────
    uploadGameZip(campaignId: string, formData: FormData) {
        const token = this.getToken();
        const url = this.buildUrl(`/admin/games/upload/${campaignId}`);
        const headers: Record<string, string> = {};
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        return fetch(url, { method: 'POST', headers, body: formData }).then(res => res.json());
    }

    getUploadTemplates() {
        return this.request<any>('/admin/games/upload/templates');
    }

    // ─── GIFTS ────────────────────────────────────────────────────────────────
    getGifts(tenantId: string, campaignId?: string, page = 1, limit = 20) {
        return this.request<any>('/admin/gifts', { params: { tenantId, campaignId, page, limit } });
    }

    createGift(data: any) {
        return this.request<any>('/admin/gifts', { method: 'POST', body: data });
    }

    updateGift(id: string, data: any) {
        return this.request<any>(`/admin/gifts/${id}`, { method: 'PATCH', body: data });
    }

    deleteGift(id: string) {
        return this.request<any>(`/admin/gifts/${id}`, { method: 'DELETE' });
    }

    updateGiftStock(id: string, totalQuantity: number) {
        return this.request<any>(`/admin/gifts/${id}/stock`, { method: 'PATCH', body: { total_quantity: totalQuantity } });
    }

    getGiftAvailability(id: string) {
        return this.request<any>(`/admin/gifts/${id}/availability`);
    }

    getGiftDistributions(id: string, page = 1, limit = 50) {
        return this.request<any>(`/admin/gifts/${id}/distributions`, { params: { page, limit } });
    }

    // ─── PLAYERS ──────────────────────────────────────────────────────────────
    searchPlayers(tenantId: string, campaignId?: string, query?: string, page = 1, limit = 20, filters: Record<string, any> = {}) {
        return this.request<any>('/admin/players', { params: { tenantId, campaignId, query, page, limit, ...filters } });
    }

    getPlayerDetails(playerId: string, tenantId: string) {
        return this.request<any>(`/admin/players/${playerId}`, { params: { tenantId } });
    }

    blockPlayers(tenantId: string, ids: string[], reason?: string) {
        return this.request<any>('/admin/players/bulk/block', { method: 'POST', body: { tenantId, ids, reason } });
    }

    unblockPlayers(tenantId: string, ids: string[]) {
        return this.request<any>('/admin/players/bulk/unblock', { method: 'POST', body: { tenantId, ids } });
    }

    deletePlayers(tenantId: string, ids: string[]) {
        return this.request<any>('/admin/players/bulk/delete', { method: 'POST', body: { tenantId, ids } });
    }

    // ─── REPORTS ──────────────────────────────────────────────────────────────
    getReportMetrics(tenantId: string, startDate?: string, endDate?: string) {
        return this.request<any>('/admin/reports/metrics', { params: { tenantId, startDate, endDate } });
    }

    getTopCampaigns(tenantId: string, limit = 10) {
        return this.request<any>('/admin/reports/top-campaigns', { params: { tenantId, limit } });
    }

    // ─── BILLING & ENTITLEMENTS ───────────────────────────────────────────────
    getBillingPlans() {
        return this.request<any>('/admin/billing/plans');
    }

    getCurrentSubscription(tenantId: string) {
        return this.request<any>('/admin/billing/current-subscription', { params: { tenantId } });
    }

    getCurrentUsage(tenantId: string) {
        return this.request<any>('/admin/usage/current', { params: { tenantId } });
    }

    getEntitlements(tenantId: string, campaignId?: string) {
        return this.request<any>('/admin/entitlements', { params: { tenantId, campaign_id: campaignId } });
    }

    getWallet() {
        return this.request<any>('/admin/wallet');
    }

    createUpgradeInvoice(tenantId: string, targetPlanCode: string, paymentMethod = 'manual') {
        return this.request<any>('/admin/billing/upgrade', { method: 'POST', body: { tenantId, targetPlanCode, paymentMethod } });
    }

    getInvoices(tenantId: string) {
        return this.request<any>('/admin/invoices', { params: { tenantId } });
    }

    markInvoicePaid(invoiceId: string, data: any) {
        return this.request<any>(`/admin/invoices/${invoiceId}/mark-paid`, { method: 'POST', body: data });
    }

    // ─── PAYOS ────────────────────────────────────────────────────────────────
    createCheckout(planCode: string) {
        return this.request<any>('/admin/billing/checkout', { method: 'POST', body: { plan_code: planCode } });
    }

    getPaymentStatus(orderCode: number | string) {
        return this.request<any>(`/admin/billing/payment-status/${orderCode}`);
    }

    // ─── AUDIT LOGS ───────────────────────────────────────────────────────────
    getAuditLogs(params: Record<string, any>) {
        return this.request<any>('/admin/audit-logs', { params });
    }

    getAuditLogStats(tenantId: string, days = 7) {
        return this.request<any>('/admin/audit-logs/stats', { params: { tenantId, days } });
    }

    // ─── DATABASE ─────────────────────────────────────────────────────────────
    resetAllDatabase() {
        return this.request<any>('/admin/database/reset-all', { method: 'POST' });
    }

    seedDemoData() {
        return this.request<any>('/admin/database/seed-demo', { method: 'POST' });
    }
}

export const api = new ApiClient();
